import { AudioPause } from '@hendrx/icons/AudioPause';
import { AudioPlaceholder } from '@hendrx/icons/AudioPlaceholder';
import { AudioPlay } from '@hendrx/icons/AudioPlay';
import { Box } from '@mantine/core';
import { clsx } from 'clsx';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WidgetType } from 'shared/common/utils/widgetsUtils';
import { StyledButton } from 'shared/components/buttons/StyledButton/StyledButton';

import classes from './Base.module.scss';
import { Placeholder } from '../../../../../../components/messages/Placeholder/Placeholder';
import { useMediaEvents } from '../../../../../hooks/useMediaEvents';

type AudioClassNames = {
  playButton?: string;
  placeholder?: string;
};

export type BaseProps = {
  id: string;
  classNames?: AudioClassNames;
  src?: string;
  title?: string;
};

export function Base(props: BaseProps) {
  const { id, src, title, classNames } = props;
  const [isPlaying, setIsPlaying] = useState(false);

  const { t } = useTranslation('widgets');

  const label = isPlaying ? t('audioWidget.pause') : t('audioWidget.play');

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const getAudioMetrics = () => {
    const audio = audioRef.current;
    if (!audio) {
      return { progressFraction: 0, totalSeconds: 0 };
    }

    const current = audio.currentTime || 0;
    const duration = audio.duration || 0;

    return {
      progressFraction: duration ? current / duration : 0,
      totalSeconds: duration
    };
  };

  const { sendMediaPlayedEvent, sendMediaPausedEvent, sendMediaStoppedEvent } = useMediaEvents(id, WidgetType.Audio);

  const handleClick = () => {
    const { progressFraction, totalSeconds } = getAudioMetrics();

    if (isPlaying) {
      audioRef?.current?.pause();
      sendMediaPausedEvent(progressFraction, totalSeconds, src ?? '');
    } else {
      void audioRef?.current?.play();
      sendMediaPlayedEvent(progressFraction, totalSeconds, src ?? '');
    }
    setIsPlaying(!isPlaying);
  };

  const handleAudioStop = () => {
    setIsPlaying(false);
    const { progressFraction, totalSeconds } = getAudioMetrics();
    sendMediaStoppedEvent(progressFraction, totalSeconds, src ?? '');
  };

  return src ? (
    <>
      <audio ref={audioRef} src={src} onEnded={handleAudioStop} title={title} />
      <Box className={classes.audioContainer}>
        <StyledButton
          role="helper"
          className={clsx(classes.audio, classNames?.playButton)}
          aria-label={label}
          label={label}
          onClick={handleClick}
        >
          {isPlaying ? <AudioPause /> : <AudioPlay />}
        </StyledButton>
      </Box>
    </>
  ) : (
    <Placeholder Icon={AudioPlaceholder} className={classNames?.placeholder} />
  );
}
